export type TNullable<T> = T | null;

export enum ELanguage {
  EN = 'en',
  ES = 'es',
  PT = 'pt',
}

// Converts snake_case to camelCase
type TSnakeToCamelCase<StringType extends string> =
  StringType extends `${infer FirstPart}_${infer SecondPart}`
    ? `${FirstPart}${Capitalize<TSnakeToCamelCase<SecondPart>>}`
    : StringType;

// Converts camelCase to snake_case
type TCamelToSnakeCase<StringType extends string> =
  StringType extends `${infer FirstChar}${infer Rest}`
    ? FirstChar extends Lowercase<FirstChar>
      ? `${FirstChar}${TCamelToSnakeCase<Rest>}`
      : `_${Lowercase<FirstChar>}${TCamelToSnakeCase<Rest>}`
    : StringType;

// Applies the correct case conversion based on the Direction flag
type TConvertCase<
  StringType extends string,
  Direction extends 'snake' | 'camel',
> = Direction extends 'camel'
  ? TSnakeToCamelCase<StringType>
  : TCamelToSnakeCase<StringType>;

// Converts arrays recursively
type TConvertArrayKeys<Type, Direction extends 'snake' | 'camel'> =
  Type extends Array<infer ArrayItemType>
    ? Array<TConvertKeys<ArrayItemType, Direction>>
    : Type;

// Converts object keys based on direction
export type TConvertObjectKeys<Type, Direction extends 'snake' | 'camel'> = {
  [Key in keyof Type as TConvertCase<Key & string, Direction>]: TConvertKeys<
    Type[Key],
    Direction
  >;
};

type TBuiltIn = Date | RegExp | File | Blob | Error | Function;

// Entry point type that handles primitives, arrays, and objects
export type TConvertKeys<
  Type,
  Direction extends 'snake' | 'camel' = 'camel',
> = Type extends TBuiltIn
  ? Type
  : Type extends Array<unknown>
    ? TConvertArrayKeys<Type, Direction>
    : Type extends object
      ? TConvertObjectKeys<Type, Direction>
      : Type;

export interface ITemplateResponsePagination {
  total: number;
  page: number;
  per_page: number;
  page_count: number;
}

export interface ITemplateResponse<T = object, M = unknown> {
  data: {
    success: boolean;
    data: T;
    meta: M;
  };
  error: TNullable<object>;
}

export type TDotNotation<T, Prefix extends string = ''> = {
  [K in keyof T]: T[K] extends Array<unknown>
    ? `${Prefix}${Extract<K, string>}`
    : T[K] extends object
      ? | `${Prefix}${Extract<K, string>}`
        | TDotNotation<T[K], `${Prefix}${Extract<K, string>}.`>
      : `${Prefix}${Extract<K, string>}`;
}[keyof T];

// Recursively converts all optional (Partial) fields to required (Required) deeply
export type TDeepRequired<T> =
  T extends Array<infer U>
    ? Array<TDeepRequired<U>>
    : T extends object
      ? {
          [P in keyof T]-?: TDeepRequired<T[P]>;
        }
      : T;
